import { useTranslation } from 'react-i18next';
import { ChevronDown, Pencil, Clock, CheckCircle, XCircle, Trash2 } from 'lucide-react';

import { Button } from '@/components/ui/button';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { cn } from '@/lib/utils';
import { useAuthStore } from '@/features/auth/stores/auth.store';
import { useExpenseMutations } from '../hooks';
import type { Expense } from '../types';

interface ExpenseActionsCellProps {
  expense: Expense;
  onEdit?: ((expense: Expense) => void) | undefined;
}

// Mirrors PaymentActionsCell — a primary Edit button joined to a dropdown that
// holds the status workflow (Set pending / Accept / Deny) and Delete.
export function ExpenseActionsCell({ expense, onEdit }: ExpenseActionsCellProps) {
  const { t } = useTranslation();
  const hasAccess = useAuthStore((state) => state.hasAccess);
  const { statusMutation, deleteMutation } = useExpenseMutations();

  const { status, id } = expense;

  const canUpdate = hasAccess('expense', 'update');
  const canDelete = hasAccess('expense', 'delete') && status !== 'ACCEPTED';
  const canSetPending =
    hasAccess('expense', 'pending') &&
    status !== 'ACCEPTED' &&
    status !== 'DENIED' &&
    status !== 'PENDING';
  const canAcceptOrDeny = status === 'PENDING' || status === 'NEW';
  const canAccept = hasAccess('expense', 'activate') && canAcceptOrDeny;
  const canDeny = hasAccess('expense', 'reject') && canAcceptOrDeny;

  return (
    <div className="flex">
      {canUpdate && (
        <Button
          type="button"
          variant="outline"
          size="sm"
          onClick={() => onEdit?.(expense)}
          className="rounded-r-none border-r-0 hover:bg-slate-100 dark:hover:bg-slate-800"
        >
          <Pencil size={16} className="text-slate-600 dark:text-slate-400" />
        </Button>
      )}

      <DropdownMenu>
        <DropdownMenuTrigger asChild>
          <Button
            type="button"
            variant="outline"
            size="sm"
            className={canUpdate ? 'rounded-l-none' : ''}
          >
            <ChevronDown size={20} />
          </Button>
        </DropdownMenuTrigger>
        <DropdownMenuContent align="end" className="min-w-[180px]">
          {/* Edit */}
          {canUpdate && (
            <DropdownMenuItem onClick={() => onEdit?.(expense)} className="cursor-pointer">
              <Pencil className="mr-2 h-4 w-4 text-slate-500" />
              <span>{t('edit')}</span>
            </DropdownMenuItem>
          )}

          {/* Set Pending */}
          {canSetPending && (
            <DropdownMenuItem
              onClick={() => statusMutation.mutate({ id, status: 'PENDING' })}
              disabled={statusMutation.isPending}
              className={cn(
                'cursor-pointer',
                'text-amber-600 dark:text-amber-500',
                'focus:text-amber-700 focus:bg-amber-50 dark:focus:bg-amber-950/50',
              )}
            >
              <Clock className="mr-2 h-4 w-4" />
              <span className="font-medium">{t('expense.submit')}</span>
            </DropdownMenuItem>
          )}

          {/* Accept */}
          {canAccept && (
            <DropdownMenuItem
              onClick={() => statusMutation.mutate({ id, status: 'ACCEPTED' })}
              disabled={statusMutation.isPending}
              className={cn(
                'cursor-pointer',
                'text-emerald-600 dark:text-emerald-500',
                'focus:text-emerald-700 focus:bg-emerald-50 dark:focus:bg-emerald-950/50',
              )}
            >
              <CheckCircle className="mr-2 h-4 w-4" />
              <span className="font-medium">{t('expense.accept')}</span>
            </DropdownMenuItem>
          )}

          {/* Deny */}
          {canDeny && (
            <DropdownMenuItem
              onClick={() => statusMutation.mutate({ id, status: 'DENIED' })}
              disabled={statusMutation.isPending}
              className={cn(
                'cursor-pointer',
                'text-orange-600 dark:text-orange-500',
                'focus:text-orange-700 focus:bg-orange-50 dark:focus:bg-orange-950/50',
              )}
            >
              <XCircle className="mr-2 h-4 w-4" />
              <span className="font-medium">{t('expense.deny')}</span>
            </DropdownMenuItem>
          )}

          {/* Delete */}
          {canDelete && (
            <>
              <DropdownMenuSeparator />
              <DropdownMenuItem
                onClick={() => deleteMutation.mutate({ id })}
                disabled={deleteMutation.isPending}
                className={cn(
                  'cursor-pointer',
                  'text-red-600 dark:text-red-500',
                  'focus:text-red-700 focus:bg-red-50 dark:focus:bg-red-950/50',
                )}
              >
                <Trash2 className="mr-2 h-4 w-4" />
                <span className="font-medium">{t('delete')}</span>
              </DropdownMenuItem>
            </>
          )}
        </DropdownMenuContent>
      </DropdownMenu>
    </div>
  );
}
